import React, { useEffect, useState } from 'react';
import {
  adminCreateContent,
  adminDeleteContent,
  adminGetContentByType,
  adminUpdateContent,
} from '../../api/adminApi';
import { ContentDocument, OpeningHoursRule } from '../../api/contentApi';

const WEEKDAYS = [
  { value: 1, label: 'Montag' },
  { value: 2, label: 'Dienstag' },
  { value: 3, label: 'Mittwoch' },
  { value: 4, label: 'Donnerstag' },
  { value: 5, label: 'Freitag' },
  { value: 6, label: 'Samstag' },
  { value: 0, label: 'Sonntag' },
];

const DEFAULT_SETTINGS: Partial<ContentDocument> = {
  type: 'booking-settings',
  isPublished: true,
  sortOrder: 0,
  slug: 'default',
  timeZoneId: 'Europe/Berlin',
  openingHours: [],
  exceptions: [],
  slotGranularityMinutes: 15,
  leadTimeMinutes: 720,
  maxAdvanceDays: 60,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  requireApproval: false,
  holdMinutes: 20,
  cancellationCutoffHours: 24,
  failClosed: true,
  includeBookingCalendarAsBlocking: false,
  createContactSubmission: true,
  translations: {},
};

const SHOW_AS_OPTIONS = ['free', 'tentative', 'busy', 'oof', 'workingElsewhere', 'unknown'];
const SHOW_AS_LABELS: Record<string, string> = {
  free: 'Frei',
  tentative: 'Mit Vorbehalt',
  busy: 'Beschäftigt',
  oof: 'Abwesend',
  workingElsewhere: 'Extern arbeitend',
  unknown: 'Unbekannt',
};

/**
 * Free-text fields are kept in local state and only PUT on blur — editing directly against
 * `settings`/`types` state fired one PUT per keystroke (and risked out-of-order responses
 * clobbering later keystrokes).
 */
const AppointmentTypeCard: React.FC<{
  type: ContentDocument;
  onSave: (patch: Partial<ContentDocument>) => void;
  onDelete: () => void;
}> = ({ type, onSave, onDelete }) => {
  const [title, setTitle] = useState(type.translations.de?.title ?? '');
  const [areaOfLaw, setAreaOfLaw] = useState(type.areaOfLaw ?? '');

  useEffect(() => { setTitle(type.translations.de?.title ?? ''); }, [type.translations.de?.title]);
  useEffect(() => { setAreaOfLaw(type.areaOfLaw ?? ''); }, [type.areaOfLaw]);

  const commitTitle = () => {
    if (title === (type.translations.de?.title ?? '')) return;
    onSave({ translations: { ...type.translations, de: { ...(type.translations.de ?? { isPublished: true }), title } } });
  };

  const commitAreaOfLaw = () => {
    if (areaOfLaw === (type.areaOfLaw ?? '')) return;
    onSave({ areaOfLaw });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 grid grid-cols-2 gap-3">
      <label className="text-sm text-gray-600 col-span-2">
        Titel (Deutsch)
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          className="block mt-1 w-full border border-gray-300 rounded px-2 py-1"
        />
      </label>
      <label className="text-sm text-gray-600">
        Dauer (Minuten)
        <input type="number" value={type.durationMinutes ?? 30} onChange={(e) => onSave({ durationMinutes: Number(e.target.value) })} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
      </label>
      <label className="text-sm text-gray-600">
        Modus
        <select value={type.mode ?? 'phone'} onChange={(e) => onSave({ mode: e.target.value })} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1">
          <option value="phone">Telefon</option>
          <option value="video">Video</option>
          <option value="onsite">Vor Ort</option>
        </select>
      </label>
      <label className="text-sm text-gray-600">
        Rechtsgebiet (optional)
        <input value={areaOfLaw} onChange={(e) => setAreaOfLaw(e.target.value)} onBlur={commitAreaOfLaw} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={type.isPublished} onChange={(e) => onSave({ isPublished: e.target.checked })} />
        Veröffentlicht
      </label>
      <div className="col-span-2 text-right">
        <button onClick={onDelete} className="text-red-500 text-sm hover:underline">Löschen</button>
      </div>
    </div>
  );
};

const AdminBookingSettings: React.FC = () => {
  const [settings, setSettings] = useState<ContentDocument | null>(null);
  const [types, setTypes] = useState<ContentDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsList, typeList] = await Promise.all([
        adminGetContentByType('booking-settings'),
        adminGetContentByType('appointment-type'),
      ]);
      setSettings(settingsList[0] ?? (DEFAULT_SETTINGS as ContentDocument));
      setTypes(typeList);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateSettingsField = <K extends keyof ContentDocument>(field: K, value: ContentDocument[K]) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: value });
  };

  const rangesFor = (weekday: number): OpeningHoursRule['ranges'] =>
    settings?.openingHours?.find((r) => r.weekday === weekday)?.ranges ?? [];

  const updateRanges = (weekday: number, ranges: OpeningHoursRule['ranges']) => {
    if (!settings) return;
    const existing = settings.openingHours ?? [];
    const withoutDay = existing.filter((r) => r.weekday !== weekday);
    const next = ranges.length > 0 ? [...withoutDay, { weekday, ranges }] : withoutDay;
    updateSettingsField('openingHours', next);
  };

  const addRange = (weekday: number) => {
    updateRanges(weekday, [...rangesFor(weekday), { start: '09:00', end: '17:00' }]);
  };

  const removeRange = (weekday: number, index: number) => {
    updateRanges(weekday, rangesFor(weekday).filter((_, i) => i !== index));
  };

  const changeRange = (weekday: number, index: number, field: 'start' | 'end', value: string) => {
    updateRanges(weekday, rangesFor(weekday).map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addException = () => {
    if (!settings) return;
    const today = new Date().toISOString().slice(0, 10);
    updateSettingsField('exceptions', [...(settings.exceptions ?? []), { date: today, closed: true }]);
  };

  const removeException = (index: number) => {
    if (!settings) return;
    updateSettingsField('exceptions', (settings.exceptions ?? []).filter((_, i) => i !== index));
  };

  const updateException = (index: number, next: NonNullable<ContentDocument['exceptions']>[number]) => {
    if (!settings) return;
    updateSettingsField('exceptions', (settings.exceptions ?? []).map((e, i) => (i === index ? next : e)));
  };

  const updateExceptionRange = (index: number, rangeIndex: number, field: 'start' | 'end', value: string) => {
    if (!settings) return;
    const ex = (settings.exceptions ?? [])[index];
    if (!ex) return;
    const ranges = (ex.ranges ?? []).map((r, i) => (i === rangeIndex ? { ...r, [field]: value } : r));
    updateException(index, { ...ex, ranges });
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    setSaveMessage('');
    try {
      const saved = settings.id
        ? await adminUpdateContent('booking-settings', settings.id, settings)
        : await adminCreateContent(settings);
      setSettings(saved);
      setSaveMessage('Gespeichert.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddType = async () => {
    try {
      const created = await adminCreateContent({
        type: 'appointment-type',
        isPublished: true,
        sortOrder: types.length,
        durationMinutes: 30,
        mode: 'phone',
        translations: { de: { isPublished: true, title: 'Neue Terminart' } },
      });
      setTypes([...types, created]);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Only checkbox/select fields commit immediately — free-text fields are saved on blur via
  // AppointmentTypeCard's local state, so typing a title doesn't fire a PUT per keystroke.
  const handleUpdateType = async (type: ContentDocument, patch: Partial<ContentDocument>) => {
    const updated = { ...type, ...patch };
    setTypes((prev) => prev.map((t) => (t.id === type.id ? updated : t)));
    try {
      const saved = await adminUpdateContent('appointment-type', type.id, updated);
      setTypes((prev) => prev.map((t) => (t.id === type.id ? saved : t)));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteType = async (type: ContentDocument) => {
    if (!window.confirm('Diese Terminart wirklich löschen?')) return;
    try {
      await adminDeleteContent('appointment-type', type.id);
      setTypes((prev) => prev.filter((t) => t.id !== type.id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <p className="text-gray-400">Lade Einstellungen…</p>;
  if (!settings) return null;

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Terminbuchung — Einstellungen</h1>
      {error && <div className="error mb-4">{error}</div>}

      <section className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4">Öffnungszeiten</h2>
        <p className="text-xs text-gray-500 mb-3">
          Puffer vor/nach Terminen wirken auf beide Seiten einer Buchung: Der Mindestabstand zwischen zwei
          Website-Buchungen ist die Summe aus Puffer&nbsp;danach und Puffer&nbsp;davor. Für einen einzelnen
          Abstand nur „Puffer danach&quot; setzen.
        </p>
        {WEEKDAYS.map((wd) => (
          <div key={wd.value} className="flex items-start gap-3 mb-2">
            <span className="w-24 pt-1.5 text-sm text-gray-600">{wd.label}</span>
            <div className="flex-1 space-y-1">
              {rangesFor(wd.value).map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="time" value={r.start} onChange={(e) => changeRange(wd.value, i, 'start', e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />
                  <span className="text-gray-400">–</span>
                  <input type="time" value={r.end} onChange={(e) => changeRange(wd.value, i, 'end', e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />
                  <button onClick={() => removeRange(wd.value, i)} className="text-red-500 text-xs hover:underline">Entfernen</button>
                </div>
              ))}
              <button onClick={() => addRange(wd.value)} className="text-primary-600 text-xs hover:underline">+ Zeitraum hinzufügen</button>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-white rounded-lg shadow p-6 mb-6 grid grid-cols-2 gap-4">
        <h2 className="col-span-2 font-semibold text-gray-800">Regeln</h2>
        <label className="text-sm text-gray-600">
          Zeitzone
          <input value={settings.timeZoneId ?? ''} onChange={(e) => updateSettingsField('timeZoneId', e.target.value)} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Rastergröße (Minuten)
          <input type="number" value={settings.slotGranularityMinutes ?? 15} onChange={(e) => updateSettingsField('slotGranularityMinutes', Number(e.target.value))} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Vorlaufzeit (Minuten)
          <input type="number" value={settings.leadTimeMinutes ?? 0} onChange={(e) => updateSettingsField('leadTimeMinutes', Number(e.target.value))} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Max. Vorausbuchung (Tage)
          <input type="number" value={settings.maxAdvanceDays ?? 60} onChange={(e) => updateSettingsField('maxAdvanceDays', Number(e.target.value))} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Puffer davor (Minuten)
          <input type="number" value={settings.bufferBeforeMinutes ?? 0} onChange={(e) => updateSettingsField('bufferBeforeMinutes', Number(e.target.value))} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Puffer danach (Minuten)
          <input type="number" value={settings.bufferAfterMinutes ?? 0} onChange={(e) => updateSettingsField('bufferAfterMinutes', Number(e.target.value))} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Max. Termine pro Tag (leer = unbegrenzt)
          <input type="number" value={settings.maxBookingsPerDay ?? ''} onChange={(e) => updateSettingsField('maxBookingsPerDay', e.target.value ? Number(e.target.value) : undefined)} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Stornofrist (Stunden)
          <input type="number" value={settings.cancellationCutoffHours ?? 24} onChange={(e) => updateSettingsField('cancellationCutoffHours', Number(e.target.value))} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Bestätigungsfrist bei Freigabe (Minuten)
          <input type="number" value={settings.holdMinutes ?? 20} onChange={(e) => updateSettingsField('holdMinutes', Number(e.target.value))} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
          <input type="checkbox" checked={!!settings.requireApproval} onChange={(e) => updateSettingsField('requireApproval', e.target.checked)} />
          Termine müssen manuell bestätigt werden (statt automatischer Sofortbestätigung)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
          <input type="checkbox" checked={settings.failClosed !== false} onChange={(e) => updateSettingsField('failClosed', e.target.checked)} />
          Bei Outlook-Ausfall keine Termine anzeigen (empfohlen, verhindert Doppelbuchungen)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
          <input type="checkbox" checked={settings.createContactSubmission !== false} onChange={(e) => updateSettingsField('createContactSubmission', e.target.checked)} />
          Termine automatisch mit Kontaktanfragen verknüpfen
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
          <input type="checkbox" checked={!!settings.includeBookingCalendarAsBlocking} onChange={(e) => updateSettingsField('includeBookingCalendarAsBlocking', e.target.checked)} />
          Termin-Kalender selbst auch als blockierend behandeln (nur für manuell bearbeitete Einträge)
        </label>
        <label className="text-sm text-gray-600 col-span-2">
          Benachrichtigungs-E-Mail für Termine (optional, überschreibt die allgemeine Adresse)
          <input type="email" value={settings.bookingNotificationEmail ?? ''} onChange={(e) => updateSettingsField('bookingNotificationEmail', e.target.value || undefined)} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>

        <div className="col-span-2">
          <span className="text-sm text-gray-600 block mb-1">Blockierende Outlook-Status</span>
          <div className="flex flex-wrap gap-3">
            {SHOW_AS_OPTIONS.map((opt) => {
              const current = settings.blockingShowAs ?? [];
              const checked = current.includes(opt);
              return (
                <label key={opt} className="flex items-center gap-1 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateSettingsField('blockingShowAs', e.target.checked ? [...current, opt] : current.filter((v) => v !== opt))}
                  />
                  {SHOW_AS_LABELS[opt] ?? opt}
                </label>
              );
            })}
          </div>
        </div>

        <div className="col-span-2">
          <button onClick={handleSaveSettings} disabled={saving} className="bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-6 rounded-lg disabled:opacity-50">
            {saving ? 'Speichern…' : 'Einstellungen speichern'}
          </button>
          {saveMessage && <span className="ml-3 text-green-700 text-sm">{saveMessage}</span>}
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4">Ausnahmen (Feiertage, Urlaub)</h2>
        {(settings.exceptions ?? []).length === 0 && (
          <p className="text-xs text-gray-400 mb-3">Keine Ausnahmen hinterlegt.</p>
        )}
        <div className="space-y-2 mb-3">
          {(settings.exceptions ?? []).map((ex, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={ex.date}
                onChange={(e) => updateException(i, { ...ex, date: e.target.value })}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input type="checkbox" checked={ex.closed} onChange={(e) => updateException(i, { ...ex, closed: e.target.checked, ranges: e.target.checked ? [] : ex.ranges })} />
                Geschlossen
              </label>
              {!ex.closed && (
                <>
                  {(ex.ranges ?? []).map((r, ri) => (
                    <span key={ri} className="flex items-center gap-1">
                      <input type="time" value={r.start} onChange={(e) => updateExceptionRange(i, ri, 'start', e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />
                      <span className="text-gray-400">–</span>
                      <input type="time" value={r.end} onChange={(e) => updateExceptionRange(i, ri, 'end', e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />
                    </span>
                  ))}
                  <button onClick={() => updateException(i, { ...ex, ranges: [...(ex.ranges ?? []), { start: '09:00', end: '17:00' }] })} className="text-primary-600 text-xs hover:underline">
                    + Zeitraum
                  </button>
                </>
              )}
              <button onClick={() => removeException(i)} className="text-red-500 text-xs hover:underline ml-auto">Entfernen</button>
            </div>
          ))}
        </div>
        <button onClick={addException} className="text-primary-600 text-sm hover:underline">+ Ausnahme hinzufügen</button>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">Terminarten</h2>
          <button onClick={handleAddType} className="text-primary-600 text-sm hover:underline">+ Neue Terminart</button>
        </div>
        {types.length === 0 && <p className="text-gray-400 text-sm">Noch keine Terminarten angelegt.</p>}
        <div className="space-y-4">
          {types.map((type) => (
            <AppointmentTypeCard
              key={type.id}
              type={type}
              onSave={(patch) => handleUpdateType(type, patch)}
              onDelete={() => handleDeleteType(type)}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminBookingSettings;
